import { FC, memo, useCallback } from 'react';
import { type EdgeProps, getSmoothStepPath } from 'reactflow';

import { ErdEdgeData } from '../types';

export const ErdRelationshipEdge: FC<EdgeProps<ErdEdgeData>> = memo(
  ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    data,
  }) => {
    const [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      borderRadius: 8,
    });

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (data?.onEdgeClick && data?.relationship) {
          data.onEdgeClick(data.relationship);
        }
      },
      [data],
    );

    const edgeSource = data?.relationship?.edgeSource;
    const bgColor =
      edgeSource === 'relationshipTest'
        ? 'bg-bgBrandMuted text-fgBrand'
        : edgeSource === 'semanticLayer'
          ? 'bg-bgSuccessMuted text-fgSuccess'
          : 'bg-bgWarningMuted text-fgWarning';

    const rel = data?.relationship;
    const labelText =
      rel?.sourceColumn && rel?.targetColumn
        ? `${rel.sourceColumn} \u2014 ${rel.targetColumn}`
        : (rel?.label ?? '');

    return (
      <>
        {/* Undirected edge — no markers/arrows */}
        <path
          id={id}
          d={edgePath}
          fill="none"
          stroke={style?.stroke as string}
          strokeWidth={style?.strokeWidth as number}
          strokeDasharray={style?.strokeDasharray as string | undefined}
          className="react-flow__edge-path"
        />
        {/* Wider invisible path for easier click target */}
        <path
          d={edgePath}
          fill="none"
          stroke="transparent"
          strokeWidth={20}
          className="cursor-pointer"
          onClick={handleClick}
          data-testid={`erd-edge-${id}`}
        />
        {labelText && (
          <foreignObject
            x={labelX - 60}
            y={labelY - 12}
            width={120}
            height={24}
            className="pointer-events-none overflow-visible"
          >
            <div className="flex items-center justify-center">
              <span
                className={`cursor-pointer truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${bgColor} pointer-events-auto max-w-[116px]`}
                onClick={handleClick}
                title={labelText}
              >
                {labelText}
              </span>
            </div>
          </foreignObject>
        )}
      </>
    );
  },
);

ErdRelationshipEdge.displayName = 'ErdRelationshipEdge';
